import { Fragment } from "react";
import { StatusBadge } from "@/components/StatusBadge";
import { PHASES, fmt, stageStyle } from "@/lib/engagements";

const StageBadge = ({ phaseKey, stage, overdue, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center whitespace-nowrap rounded-sm border px-2 py-0.5 text-xs font-medium ${stageStyle(
      phaseKey,
      stage,
      overdue
    )}`}
  >
    {stage}
    {overdue && stage !== "n/a" ? " · overdue" : ""}
  </span>
);

export const EngagementTable = ({ items, loading, onRowClick }) => {
  if (loading) {
    return (
      <div className="border border-border">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-10 animate-pulse border-b border-border bg-zinc-50" />
        ))}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div data-testid="empty-state" className="border border-border bg-white p-12 text-center">
        <p className="font-display text-lg font-bold">No engagements match your filters</p>
        <p className="mt-1 text-sm text-zinc-500">Adjust the search, or import your workbook.</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-x-auto border border-border bg-white">
      <table data-testid="engagement-table" className="min-w-full border-collapse text-left">
        <thead>
          <tr className="bg-zinc-900 text-white">
            <th className="border-r border-zinc-700 px-3 py-2.5 text-[10px] uppercase tracking-widest">Client</th>
            <th className="border-r border-zinc-700 px-3 py-2.5 text-[10px] uppercase tracking-widest">Year End</th>
            <th className="border-r-2 border-zinc-500 px-3 py-2.5 text-[10px] uppercase tracking-widest">Overall</th>
            {PHASES.map((p) => (
              <th
                key={p.key}
                data-testid={`group-header-${p.key}`}
                colSpan={2}
                className="border-r-2 border-zinc-500 px-3 py-2.5 text-center text-[10px] uppercase tracking-widest"
              >
                {p.label}
              </th>
            ))}
          </tr>
          <tr className="bg-zinc-100 text-zinc-600">
            <th className="border-r border-border px-3 py-1.5" />
            <th className="border-r border-border px-3 py-1.5" />
            <th className="border-r-2 border-zinc-300 px-3 py-1.5" />
            {PHASES.map((p) => (
              <Fragment key={p.key}>
                <th className="border-r border-border px-3 py-1.5 text-[10px] font-medium">
                  PIC
                </th>
                <th className="border-r-2 border-zinc-300 px-3 py-1.5 text-[10px] font-medium">
                  Status
                </th>
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr
              key={e.id}
              data-testid={`engagement-row-${e.id}`}
              onClick={() => onRowClick(e)}
              className="cursor-pointer border-b border-border transition-colors hover:bg-zinc-50"
            >
              <td className="whitespace-nowrap border-r border-border px-3 py-2.5 text-[13px] font-semibold">
                {e.client}
              </td>
              <td className="whitespace-nowrap border-r border-border px-3 py-2.5 text-xs tabular">
                {fmt(e.year_end)}
              </td>
              <td className="whitespace-nowrap border-r-2 border-zinc-300 px-3 py-2.5">
                <StatusBadge status={e.status} testid={`status-${e.id}`} />
              </td>
              {PHASES.map((p) => {
                const off = e.services && e.services[p.key] === false;
                const st = e.phase_status?.[p.key];
                return (
                  <Fragment key={`${e.id}-${p.key}`}>
                    <td
                      className={`whitespace-nowrap border-r border-border px-3 py-2.5 text-xs ${
                        off ? "bg-zinc-50 text-zinc-400" : "text-zinc-700"
                      }`}
                    >
                      {off ? "n/a" : e[p.key]?.pic || "—"}
                    </td>
                    <td
                      className={`whitespace-nowrap border-r-2 border-zinc-300 px-3 py-2.5 ${
                        off ? "bg-zinc-50" : ""
                      }`}
                    >
                      {off ? (
                        <span className="text-xs text-zinc-400">n/a</span>
                      ) : (
                        <StageBadge
                          phaseKey={p.key}
                          stage={st}
                          overdue={e.phase_overdue?.[p.key]}
                          testid={`phase-status-${e.id}-${p.key}`}
                        />
                      )}
                    </td>
                  </Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
